import { isSupported } from "@twilio/video-processors";
import { LocalVideoTrack } from "twilio-video";
import { logger } from "../../../../datadog/logger";
import { BackgroundSettings, BackgroundType } from "../../../core/types";
import { TrackOperationResult } from "../types";
import {
  addProcessor,
  blurProcessor,
  getBackgroundImage,
  loadProcessorModels,
  removeProcessor,
  virtualBackgroundProcessor,
} from "./processors";
import { SetStoredAudioVideoSettings } from "../../../core/hooks/useStoredAudioVideoSettings";

export type InitializeBackgroundResult = TrackOperationResult & {
  error?: Error;
};

export async function initializeBackground(
  backgroundSettings: BackgroundSettings,
  setStoredAudioVideoSettings: SetStoredAudioVideoSettings,
  videoTrack?: LocalVideoTrack,
  options?: {
    isSupportedOverride?: boolean;
    persistChanges?: boolean;
  },
): Promise<InitializeBackgroundResult> {
  const isNotSupported = !(options?.isSupportedOverride ?? isSupported);
  const shouldPersistChanges = options?.persistChanges ?? true;

  if (isNotSupported) {
    const error = new Error("Background processor(s) not supported");
    logger.error("Background processor(s) not supported");
    return { success: false, error };
  }

  if (!videoTrack) {
    const error = new Error("No video track supplied to processor");
    logger.error("No video track supplied to processor.");
    return { success: false, error };
  }
  const { backgroundIndex, backgroundType } = backgroundSettings;

  const storeBackgroundSettings = () => {
    setStoredAudioVideoSettings((previousValue) => ({
      ...previousValue,
      ...backgroundSettings,
    }));
  };

  const addBackground = async () => {
    if (!videoTrack) return; // this is to appease TS; we already do this check above

    switch (backgroundType) {
      case BackgroundType.BLUR:
        if (!blurProcessor) break;
        addProcessor(videoTrack, blurProcessor);
        break;
      case BackgroundType.IMAGE:
        if (!virtualBackgroundProcessor) break;
        virtualBackgroundProcessor.backgroundImage = await getBackgroundImage(
          backgroundIndex ?? 0,
        );
        addProcessor(videoTrack, virtualBackgroundProcessor);
        break;
    }

    if (shouldPersistChanges) storeBackgroundSettings();
  };

  const removeBackground = async () => {
    if (!videoTrack) return; // this is to appease TS; we already do this check above
    removeProcessor(videoTrack);

    if (shouldPersistChanges) storeBackgroundSettings();
  };

  try {
    await loadProcessorModels();

    const hasBackground =
      backgroundType && backgroundType !== BackgroundType.NONE;

    if (hasBackground) {
      await addBackground();
    } else {
      await removeBackground();
    }

    return { success: true, track: videoTrack };
  } catch (error) {
    logger.error(
      "Unable to init/add background processor(s)",
      {},
      error as Error,
    );
    return { success: false, track: videoTrack, error: error as Error };
  }
}
